import { useState } from 'react';
import { Banknote, FileCheck, Loader2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { CurrencyInput } from '../ui/currency-input';
import { useToast } from '../../contexts/ToastContext';
import {
  useMarkEntryPaid,
  type PaymentStatus,
  type LedgerFeeBreakdown,
} from '../../api/extensions/useAdminEntries';

function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount,
    );
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const round2 = (n: number) => Math.round(n * 100) / 100;

interface MarkEntryPaidButtonProps {
  registrationId: string;
  paymentStatus: PaymentStatus;
  isCancelled: boolean;
  amountDue: number;
  // Fee breakdown for the entry. When present, the confirm step exposes a
  // waive-fee toggle + inline fee editor so the offline payment can be
  // recorded with the surcharge dropped or adjusted. Absent on legacy rows —
  // the control then falls back to recording the full amount due as-is.
  feeBreakdown?: LedgerFeeBreakdown | null;
}

/**
 * Admin/delegate control to record an entry fee as paid offline (cash or
 * check). Shared by the admin Entries Ledger and the user-details Entries tab.
 *
 * Only renders when there's actually an unpaid balance to settle — paid,
 * free, and cancelled entries don't show it. Uses a two-step inline confirm
 * (pick method → confirm amount) rather than a modal so it sits naturally
 * inside an entry's detail panel; recording a payment is irreversible from
 * here (the existing Stripe-refund tooling handles reversals), so the explicit
 * confirm step matters.
 *
 * The confirm step also lets the admin waive the processing surcharge or
 * override it (cash/check don't incur Stripe's fee, so waiving is common) —
 * the chosen fee replaces the surcharge in the recorded total and realigns the
 * entry's owed amount server-side so it settles fully paid.
 */
export function MarkEntryPaidButton({
  registrationId,
  paymentStatus,
  isCancelled,
  amountDue,
  feeBreakdown,
}: MarkEntryPaidButtonProps) {
  const { showSuccess, showError } = useToast();
  const markPaid = useMarkEntryPaid();
  const [pendingMethod, setPendingMethod] = useState<'cash' | 'check' | null>(
    null,
  );
  // Fee-control state, only meaningful when `feeBreakdown` is present.
  const [waiveFee, setWaiveFee] = useState(false);
  const [editingFee, setEditingFee] = useState(false);
  const [feeDraft, setFeeDraft] = useState('');
  const [editingEntryFee, setEditingEntryFee] = useState(false);
  const [entryDraft, setEntryDraft] = useState('');

  // Nothing owed / not settleable here → render nothing.
  if (isCancelled) return null;
  if (paymentStatus !== 'unpaid' && paymentStatus !== 'partial') return null;
  if (!(amountDue > 0)) return null;

  const hasFeeControls = !!feeBreakdown;
  const currentEntryFee = feeBreakdown ? feeBreakdown.entry_fee || 0 : 0;
  const nonMemberFee = feeBreakdown ? feeBreakdown.non_member_fee || 0 : 0;
  const currentFee = feeBreakdown ? feeBreakdown.transaction_fee || 0 : 0;

  // Parsed transaction-fee draft (null while not editing / draft blank).
  const parsedOverride =
    editingFee && feeDraft.trim() !== '' ? parseFloat(feeDraft) : null;
  const overrideValid = parsedOverride != null && Number.isFinite(parsedOverride);
  // The surcharge that will actually be recorded.
  const chosenFee = waiveFee
    ? 0
    : overrideValid
      ? Math.max(0, round2(parsedOverride as number))
      : currentFee;
  const overrideChanged =
    overrideValid && Math.abs((parsedOverride as number) - currentFee) > 0.005;

  // Parsed entry-fee draft (null while not editing / draft blank).
  const parsedEntry =
    editingEntryFee && entryDraft.trim() !== '' ? parseFloat(entryDraft) : null;
  const entryValid = parsedEntry != null && Number.isFinite(parsedEntry);
  const chosenEntryFee = entryValid
    ? Math.max(0, round2(parsedEntry as number))
    : currentEntryFee;
  const entryChanged =
    entryValid && Math.abs((parsedEntry as number) - currentEntryFee) > 0.005;

  // What the contestant will be recorded as paying. With a breakdown the total
  // is rebuilt from its parts (entry + non-member + chosen surcharge) so the
  // recorded amount is exactly what's shown and the transaction fee is included
  // unless waived. Without a breakdown, fall back to the raw amount due.
  const recordAmount = hasFeeControls
    ? round2(chosenEntryFee + nonMemberFee + chosenFee)
    : amountDue;

  const resetFeeState = () => {
    setWaiveFee(false);
    setEditingFee(false);
    setFeeDraft('');
    setEditingEntryFee(false);
    setEntryDraft('');
  };

  const cancel = () => {
    setPendingMethod(null);
    resetFeeState();
  };

  const confirm = async () => {
    if (!pendingMethod) return;
    try {
      const res = await markPaid.mutateAsync({
        registrationId,
        method: pendingMethod,
        // WYSIWYG: when the fee controls are present, always send the resolved
        // breakdown so the backend records exactly the displayed total — the
        // transaction fee is included by default (it follows the toggle, not
        // the legacy "check drops the fee" behavior).
        ...(hasFeeControls
          ? waiveFee
            ? { waiveTransactionFee: true, entryFeeOverride: chosenEntryFee }
            : {
                feeOverride: chosenFee,
                entryFeeOverride: chosenEntryFee,
              }
          : {}),
      });
      const label = pendingMethod === 'cash' ? 'cash' : 'check';
      let msg = `Recorded ${formatCurrency(res.amount)} paid by ${label}.`;
      if (hasFeeControls && waiveFee) {
        msg += ' Transaction fee waived.';
      } else if (hasFeeControls && overrideChanged) {
        msg += ` Transaction fee set to ${formatCurrency(chosenFee)}.`;
      }
      if (hasFeeControls && entryChanged) {
        msg += ` Entry fee set to ${formatCurrency(chosenEntryFee)}.`;
      }
      if (res.cancelled_stripe_payment_ids.length > 0) {
        msg += ' A pending online payment was cancelled.';
      }
      showSuccess('Entry marked paid', msg);
      cancel();
    } catch (err: any) {
      showError(
        'Could not mark entry paid',
        err?.message || 'Please try again.',
      );
    }
  };

  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        Record offline payment
      </div>
      {pendingMethod ? (
        <div className="space-y-3">
          {hasFeeControls ? (
            <div className="rounded-md border border-border/70 bg-background/50 p-2.5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">Entry fee</span>
                {editingEntryFee ? (
                  <div className="w-28">
                    <CurrencyInput
                      value={entryDraft}
                      onChange={setEntryDraft}
                      disabled={markPaid.isPending}
                      aria-label="Entry fee amount"
                    />
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {formatCurrency(chosenEntryFee)}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => {
                        setEditingEntryFee(true);
                        setEntryDraft(currentEntryFee.toFixed(2));
                      }}
                      disabled={markPaid.isPending}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </Button>
                  </span>
                )}
              </div>
              {nonMemberFee > 0 ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    Non-member fee
                  </span>
                  <span className="text-sm font-semibold">
                    {formatCurrency(nonMemberFee)}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">Waive transaction fee</span>
                <Switch
                  checked={waiveFee}
                  disabled={markPaid.isPending}
                  onCheckedChange={(next) => {
                    setWaiveFee(next);
                    // Waiving and editing are mutually exclusive.
                    if (next) {
                      setEditingFee(false);
                      setFeeDraft('');
                    }
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">
                  Transaction fee
                </span>
                {waiveFee ? (
                  <span className="text-sm font-semibold line-through text-muted-foreground">
                    {formatCurrency(currentFee)}
                  </span>
                ) : editingFee ? (
                  <div className="w-28">
                    <CurrencyInput
                      value={feeDraft}
                      onChange={setFeeDraft}
                      disabled={markPaid.isPending}
                      aria-label="Transaction fee amount"
                    />
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {formatCurrency(chosenFee)}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => {
                        setEditingFee(true);
                        setFeeDraft(currentFee.toFixed(2));
                      }}
                      disabled={markPaid.isPending}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </Button>
                  </span>
                )}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm">
              Record{' '}
              <span className="font-semibold">
                {formatCurrency(recordAmount)}
              </span>{' '}
              paid by{' '}
              <span className="font-semibold">
                {pendingMethod === 'cash' ? 'cash' : 'check'}
              </span>
              ?
            </span>
            <Button size="sm" onClick={confirm} disabled={markPaid.isPending}>
              {markPaid.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancel}
              disabled={markPaid.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPendingMethod('cash')}
          >
            <Banknote className="h-4 w-4 mr-1.5" />
            Mark paid by cash
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPendingMethod('check')}
          >
            <FileCheck className="h-4 w-4 mr-1.5" />
            Mark paid by check
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2 leading-snug">
        Confirms the entry and clears it from the contestant&apos;s outstanding
        balance. Records who recorded it and when.
      </p>
    </div>
  );
}
